/**
 * Página de lista de Productos — ListView + useListViews + useStandardListFilters.
 * Misma estructura que Categorías/Modificadores: vistas, búsqueda, ordenación, editar columnas.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BlockStack,
  ChoiceList,
  IndexFiltersMode,
  Text,
  useSetIndexFiltersMode,
} from '@/lib/polaris';
import { evaluarBorrado } from '@/lib/products/productDeletion';
import { deleteAsset } from '@/lib/storage';
import { supabase } from '@/lib/supabase';
import { useAuth, usePagination } from '@smarty/shared';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { useProductsData } from '@/hooks/useProductsData';
import {
  fetchProductsStats, fetchAllProductsMatching, fetchProductsByIds,
  type ProductRow, type ProductSortKey, type ProductStatusView, type ProductMarketplaceState,
} from '@/lib/productsApi';
import { ListView, buildListViewPagination } from '@/components/admin/ListView';
import { getProductsListConfig } from '@/config/listViews';
import type { ProductListItem } from '@/config/listViews';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { routeHelpers } from '@/config/routes.config';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { exportListToExcel, getExportFilename } from '@/lib/exportToExcel';
import { ProductIcon } from '@/lib/polaris-icons';
import { useListViews } from '@/hooks/useListViews';
import { useCanManage } from '@/hooks/useCanManage';
import { useStandardListFilters } from '@/hooks/useStandardListFilters';

const LIST_ID = 'products';

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nombre A-Z' },
  { value: 'name_desc', label: 'Nombre Z-A' },
  { value: 'category_asc', label: 'Categoría A-Z' },
  { value: 'category_desc', label: 'Categoría Z-A' },
] as const;

const SORT_OPTIONS_POLARIS = SORT_OPTIONS.map((o) => ({
  label: o.label,
  value: o.value.replace('_', ' ') as `${string} ${'asc' | 'desc'}`,
  directionLabel: o.label,
}));

const STATUS_FILTER_OPTIONS = [
  { label: 'Activo', value: 'active' },
  { label: 'Inactivo', value: 'inactive' },
] as const;

function toListItem(
  p: ProductRow,
  inventoryTotalByProductId: Record<string, number>,
  marketplaceByProductId: Record<string, ProductMarketplaceState>
): ProductListItem {
  const inventoryDisplay =
    inventoryTotalByProductId[p.id] !== undefined
      ? `${inventoryTotalByProductId[p.id]} en inventario`
      : p.stock != null
        ? `${p.stock} disponibles`
        : '—';
  const tienda = !!p.show_in_store;
  const pos = !!p.is_active;
  const n = (tienda ? 1 : 0) + (pos ? 1 : 0);
  const parts: string[] = [];
  if (tienda) parts.push('Tienda');
  if (pos) parts.push('POS');
  const channelsDisplay =
    n === 0 ? '0 canales' : n === 1 ? `1 canal (${parts[0]})` : `${n} canales (${parts.join(', ')})`;
  return {
    id: p.id,
    name: p.name,
    image_url: p.image_url || '/placeholder.svg',
    category: p.category || '',
    is_active: p.is_active,
    show_in_store: p.show_in_store,
    stock: p.stock,
    price: p.price,
    compare_at_price: p.compare_at_price,
    inventoryDisplay,
    channelsDisplay,
    // Campos normalizados que alimentan el desplegable de Canales.
    _channel_store: tienda,
    _channel_pos: pos,
    _channel_marketplaces: marketplaceByProductId[p.id] ?? {},
  };
}

export default function ProductsPage() {
  const { tenantId, loading: authLoading } = useAuth();
  const canManage = useCanManage();
  const navigate = useStoreNavigate();
  const toast = useAdminToast();

  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0, archived: 0 });

  const [searchValue, setSearchValue] = useState('');
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterCategory, setFilterCategory] = useState<string[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [sortOption, setSortOption] = useState<string>(SORT_OPTIONS[0].value);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);

  const pagination = usePagination({ total: null });

  const filtersRecord = useMemo(
    () => ({ status: filterStatus, category: filterCategory }),
    [filterStatus, filterCategory]
  );
  const setFiltersFromView = useCallback((f: Record<string, string[]>) => {
    setFilterStatus(f.status ?? []);
    setFilterCategory(f.category ?? []);
  }, []);

  const PRODUCTS_SYSTEM_VIEWS = useMemo(
    () => [
      {
        id: 'todos',
        label: `Todos (${stats.total})`,
        isSystem: true,
        query: '',
        filterValues: { status: [] as string[], category: [] as string[] },
      },
      {
        id: 'activos',
        label: `Activos (${stats.active})`,
        isSystem: true,
        query: '',
        filterValues: { status: ['active'] as string[], category: [] as string[] },
      },
      {
        id: 'borradores',
        label: `Borradores (${stats.draft})`,
        isSystem: true,
        query: '',
        filterValues: { status: ['inactive'] as string[], category: [] as string[] },
      },
      {
        // 🪤 Esta pestaña filtraba por `status: inactive` — exactamente igual que "Borradores", así
        // que las dos mostraban lo mismo. Ahora el estado archivado es propio y lo resuelve la vista
        // (`statusView`), no el filtro de activo/inactivo.
        id: 'archivados',
        label: `Archivados (${stats.archived})`,
        isSystem: true,
        query: '',
        filterValues: { status: [] as string[], category: [] as string[] },
      },
    ],
    [stats.total, stats.active, stats.draft, stats.archived]
  );

  const views = useListViews({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    systemViews: PRODUCTS_SYSTEM_VIEWS,
    searchValue,
    filters: filtersRecord,
    setSearchValue,
    setFilters: setFiltersFromView,
    setMode,
    mode,
  });
  const { activeView, selectedTabIndex, handleSwitchView } = views;

  const config = useMemo(() => getProductsListConfig(navigate), [navigate]);

  const appliedFilters = useMemo(() => {
    const items: { key: string; label: string; onRemove: () => void }[] = [];
    if (filterStatus.length > 0) {
      const labels = filterStatus
        .map((v) => STATUS_FILTER_OPTIONS.find((o) => o.value === v)?.label ?? v)
        .join(', ');
      items.push({
        key: 'status',
        label: `Estado: ${labels}`,
        onRemove: () => setFilterStatus([]),
      });
    }
    if (filterCategory.length > 0) {
      const labels = filterCategory
        .map((id) => categories.find((c) => c.name === id)?.name ?? id)
        .join(', ');
      items.push({
        key: 'category',
        label: `Categoría: ${labels}`,
        onRemove: () => setFilterCategory([]),
      });
    }
    return items;
  }, [filterStatus, filterCategory, categories]);

  const indexFiltersFilters = useMemo(
    () => [
      {
        key: 'status',
        label: 'Estado',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Estado"
            titleHidden
            choices={STATUS_FILTER_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            selected={filterStatus}
            onChange={setFilterStatus}
            allowMultiple
          />
        ),
      },
      {
        key: 'category',
        label: 'Categoría',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Categoría"
            titleHidden
            choices={categories.map((c) => ({ label: c.name, value: c.name }))}
            selected={filterCategory}
            onChange={setFilterCategory}
            allowMultiple
          />
        ),
      },
    ],
    [filterStatus, filterCategory, categories]
  );

  const clearFilters = useCallback(() => handleSwitchView(0), [handleSwitchView]);

  const {
    filterControl,
    columnOrder: tableColumnOrder,
    hiddenColumnKeys: tableHiddenKeys,
    editColumnsMode,
    editingColumnPrefs,
    onColumnOrderChange,
    onToggleColumnVisibility,
  } = useStandardListFilters({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    config,
    views,
    searchValue,
    setSearchValue,
    queryPlaceholder: activeView ? `Buscando en ${activeView.label}` : 'Buscar productos',
    filters: indexFiltersFilters,
    appliedFilters,
    onClearAll: clearFilters,
    sortOptions: SORT_OPTIONS_POLARIS,
    sortSelected: [sortOption.replace('_', ' ')],
    onSort: (v) => setSortOption((v[0] ?? sortOption).replace(' ', '_')),
    mode,
    setMode,
  });

  // Vista de estado (pestaña) → filtro server-side. Espeja el switch en memoria de antes:
  // 0 Todos · 1 Activos · 2 Borradores · 3 Archivados.
  const statusView: ProductStatusView =
    (['all', 'active', 'draft', 'archived'] as const)[selectedTabIndex] ?? 'all';
  const [sortKeyRaw, sortDirRaw] = sortOption.includes('_') ? sortOption.split('_') : [sortOption, 'asc'];
  const sortBy: ProductSortKey = sortKeyRaw === 'category' ? 'category' : 'name';
  const sortDir: 'asc' | 'desc' = sortDirRaw === 'desc' ? 'desc' : 'asc';

  const handleProductsError = useCallback(
    (msg: string) => toast.error('Error al cargar productos', msg),
    [toast]
  );

  // SERVER-SIDE: pide SOLO la página visible (offset/pageSize) + count exacto. Antes traía el
  // catálogo entero y filtraba/ordenaba/paginaba en memoria → truncaba en 1000 (búsqueda y
  // paginación rotas más allá del #1000). Ahora el tamaño del catálogo ya no importa.
  const {
    products,
    total,
    inventoryTotalByProductId,
    marketplaceByProductId,
    loading,
    reload: reloadProducts,
  } = useProductsData({
    tenantId: tenantId ?? null,
    authLoading,
    offset: pagination.offset,
    pageSize: pagination.pageSize,
    search: searchValue,
    statusView,
    statusFilter: filterStatus,
    categories: filterCategory,
    sortBy,
    sortDir,
    onError: handleProductsError,
  });

  const { setTotal } = pagination;
  useEffect(() => {
    setTotal(total);
  }, [total, setTotal]);

  // Conteos de las pestañas: exactos (count/head) sobre el conjunto COMPLETO, no truncan.
  const loadStats = useCallback(async () => {
    if (!tenantId || authLoading) return;
    try {
      setStats(await fetchProductsStats(tenantId));
    } catch (error) {
      toast.error('Error al cargar estadísticas', error instanceof Error ? error.message : undefined);
    }
  }, [tenantId, authLoading, toast]);

  const loadCategories = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .order('name');
      if (error) throw error;
      setCategories((data ?? []) as { id: string; name: string }[]);
    } catch (err) {
      toast.error('Error', err instanceof Error ? err.message : 'Error al cargar categorías.');
    }
  }, [tenantId]);

  useEffect(() => {
    if (!authLoading && tenantId) loadStats();
  }, [authLoading, tenantId, loadStats]);

  useEffect(() => {
    if (!authLoading && tenantId) loadCategories();
  }, [authLoading, tenantId, loadCategories]);

  useEffect(() => {
    pagination.goToFirstPage();
  }, [selectedTabIndex, searchValue, sortOption, filterStatus, filterCategory]);

  const handleExport = async () => {
    if (!tenantId) return;
    try {
      // Trae TODO lo que matchea el filtro (paginado en el server), no solo la página visible.
      const all = await fetchAllProductsMatching({
        tenantId, search: searchValue, statusView,
        statusFilter: filterStatus, categories: filterCategory, sortBy, sortDir,
      });
      if (all.length === 0) {
        toast.info('No hay productos para exportar');
        return;
      }
      // El inventario multi-sucursal solo se calcula para la página visible; en el export se
      // muestra el stock del producto (toListItem cae a `stock` con el mapa vacío). Los
      // marketplaces van vacíos por lo mismo: alimentan el desplegable, y el Excel exporta la
      // columna resumen, no el popover.
      const items = all.map((p) => toListItem(p, {}, {}));
      exportListToExcel({
        columns: config.columns,
        items: items as unknown as Record<string, unknown>[],
        filename: getExportFilename(config.title),
      });
      toast.success('Exportado', `${items.length} producto(s) exportado(s) a Excel`);
    } catch (error) {
      toast.error('Error al exportar', error instanceof Error ? error.message : undefined);
    }
  };
  const handleImport = () => toast.info('Función de importar en desarrollo');

  const handleBulkAction = useCallback(
    async (actionKey: string, selectedIds: string[]) => {
      if (actionKey === 'export' && selectedIds.length > 0) {
        if (!tenantId) return;
        try {
          const rows = await fetchProductsByIds(tenantId, selectedIds);
          const items = rows.map((p) => toListItem(p, {}, {}));
          exportListToExcel({
            columns: config.columns,
            items: items.map((i) => ({ ...i })),
            filename: getExportFilename(config.title),
          });
          toast.success('Exportado', `${items.length} producto(s) exportado(s) a Excel`);
        } catch (error) {
          toast.error('Error al exportar', error instanceof Error ? error.message : undefined);
        }
        return;
      }
      if (selectedIds.length === 0) {
        toast.info('Selecciona al menos un producto');
        return;
      }
      if (!tenantId) return;
      try {
        if (actionKey === 'activate') {
          const { error } = await supabase
            .from('products')
            .update({ is_active: true })
            .in('id', selectedIds)
            .eq('tenant_id', tenantId);
          if (error) throw error;
          toast.success(`${selectedIds.length} producto(s) activado(s)`);
        } else if (actionKey === 'deactivate') {
          const { error } = await supabase
            .from('products')
            .update({ is_active: false })
            .in('id', selectedIds)
            .eq('tenant_id', tenantId);
          if (error) throw error;
          toast.success(`${selectedIds.length} producto(s) desactivado(s)`);
        }
        setSelectedProducts([]);
        await reloadProducts();
        await loadStats();
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : 'Error al aplicar la acción');
      }
    },
    [config.columns, config.title, tenantId, reloadProducts, loadStats, toast]
  );

  /**
   * Qué se pierde de verdad al borrar estos productos — el MISMO criterio que la ficha del producto.
   *
   * POR QUÉ: borrar arrastra 16 tablas en cascada, entre ellas el historial de inventario y el
   * vínculo con los marketplaces. Y `order_lines` guarda su copia del nombre y el precio sin clave
   * foránea, así que el pedido sobrevive pero deja de estar asociado: los reportes por producto
   * pierden esas ventas. "Esta acción no se puede deshacer" no dice nada de eso.
   */
  const bulkDeleteConsequences = useCallback(async (ids: string[]) => {
    if (!tenantId || ids.length === 0) return {};
    const [ventas, meli, falabella, movimientos] = await Promise.all([
      supabase.from('order_lines').select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).in('product_id', ids),
      supabase.from('meli_item_links').select('product_id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).in('product_id', ids),
      supabase.from('falabella_listings').select('product_id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).in('product_id', ids),
      supabase.from('inventory_movements').select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).in('product_id', ids),
    ]);
    const veredicto = evaluarBorrado({
      ventas: ventas.count ?? 0,
      publicaciones: (meli.count ?? 0) + (falabella.count ?? 0),
      movimientos: movimientos.count ?? 0,
    });
    return {
      description: (
        <BlockStack gap="150">
          {veredicto.consecuencias.map((c) => (
            <Text as="p" variant="bodySm" tone={veredicto.nivel === 'con_historial' ? 'critical' : 'subdued'} key={c}>{c}</Text>
          ))}
        </BlockStack>
      ),
      // En masivo no se puede pedir el nombre de cada uno: se pide la palabra. La fricción sigue
      // existiendo donde hay consecuencia, y desaparece donde no la hay.
      ...(veredicto.exigeEscribirNombre ? { requireConfirmText: 'ELIMINAR' } : {}),
    };
  }, [tenantId]);

  // Borrado masivo: la plantilla pone la acción destructiva, el modal y el toast; acá el borrado real + refresco.
  const bulkDeleteProducts = useCallback(async (ids: string[]) => {
    if (!tenantId) return 0;
    /**
     * 🪤 Las fotos se leen ANTES de borrar: después la fila ya no existe y quedarían huérfanas en
     * R2, ocupando espacio para siempre. Es lo que venía pasando acá.
     */
    const { data: previos } = await supabase.from('products')
      .select('image_url, image_urls').in('id', ids).eq('tenant_id', tenantId);
    const { error } = await supabase.from('products').delete().in('id', ids).eq('tenant_id', tenantId);
    if (error) throw error;
    const fotos = new Set<string>();
    for (const p of previos ?? []) {
      for (const u of [...(p.image_urls ?? []), p.image_url]) if (u) fotos.add(u);
    }
    for (const u of fotos) void deleteAsset(u, tenantId);
    await reloadProducts();
    await loadStats();
    return ids.length;
  }, [tenantId, reloadProducts, loadStats]);

  const handleEdit = useCallback(
    (item: ProductListItem) => navigate(routeHelpers.productEdit(item.id)),
    [navigate]
  );

  const listItems = products.map((p) => toListItem(p, inventoryTotalByProductId, marketplaceByProductId));

  const paginationOption = buildListViewPagination(pagination);

  const secondaryActions = [
    { content: 'Exportar', onAction: handleExport },
    { content: 'Importar', onAction: handleImport },
    {
      content: 'Más acciones',
      actions: [
        // "Eliminar seleccionados" ahora vive en la barra de selección (plantilla, prop bulkDelete).
        { content: 'Activar seleccionados', onAction: () => handleBulkAction('activate', selectedProducts) },
        { content: 'Desactivar seleccionados', onAction: () => handleBulkAction('deactivate', selectedProducts) },
      ],
    },
  ];

  const pageTitleNode = (
    <PageBreadcrumbTitle
      icon={ProductIcon}
      title={config.title}
      onBack={config.onBack}
      backAccessibilityLabel={config.backAccessibilityLabel ?? 'Volver a Productos'}
    />
  );

  return (
    <>
      <ListView<ProductListItem>
        config={config}
        items={listItems}
        // Mismo ancho que la lista de Ventas (`OperationsOrdersView`): son las dos listas más
        // densas del admin. Con 8 columnas —producto, dos precios, estado, inventario, categoría,
        // canales, acciones— el ancho por defecto obliga a scroll horizontal para llegar a Canales.
        fullWidth
        loading={loading && products.length === 0}
        loadingMessage="Cargando productos…"
        pageTitle={pageTitleNode}
        contentBefore={filterControl}
        contentBeforeInsideCard
        selectedIds={selectedProducts}
        onSelectionChange={setSelectedProducts}
        onRowEdit={handleEdit}
        onBulkAction={handleBulkAction}
        bulkDelete={{ onDelete: bulkDeleteProducts, can: canManage, consequences: bulkDeleteConsequences }}
        columnOrder={tableColumnOrder}
        hiddenColumnKeys={tableHiddenKeys}
        editColumnsMode={editColumnsMode}
        columnOrderForEdit={editingColumnPrefs?.columnOrder?.length ? editingColumnPrefs.columnOrder : tableColumnOrder}
        onColumnOrderChange={onColumnOrderChange}
        onToggleColumnVisibility={onToggleColumnVisibility}
        pagination={paginationOption}
        secondaryActions={secondaryActions}
      />
    </>
  );
}
